import React, { useState, useRef, useEffect } from "react";
import {
  Paperclip,
  Smile,
  DollarSign,
  Plus,
  Send,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Socket } from "socket.io-client";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import { useUserInfo } from "context/user";
import { Contact } from "pages/Location/Contacts/components/DataTypes";
import { useSubAccountInfo } from "context/sub_account";

interface propsType {
  activeCoversation: any;
  contact:Contact
}

const ChatFooter = ({ activeCoversation,contact}: propsType) => {
  const {user}=useUserInfo()
  const {subaccount}=useSubAccountInfo()
  const tabs = [
    {
      name: "SMS",
    },
    {
      name: "Email",
    },
  ];
  const {contactId,locationId}=useParams()
  const AppService = useAppServices();
  const [activeTab, setActiveTab] = useState("SMS");
  const [showArrows, setShowArrows] = useState(false);
  const [message, setMessage] = useState(""); // Add message state
  const scrollRef = useRef<HTMLDivElement>(null);

  // Detect overflow
  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current;
        setShowArrows(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, []);

  // Ensure tabs start from beginning on load
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0 });
    }
  }, []);

  const scrollTabs = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Handle send message function
  const handleSend = async () => {
    if (!message.trim()) {
      alert("Please enter a message");
      return;
    }
    if(!activeCoversation){
        const { response } = await AppService.conversation.create({
      payload: {
        contactId,
        locationId,
      },
    });
    if(!response) return
    }
    let sim=user?.connectedSim || subaccount?.defaultSim
    const { response } = await AppService.message.create({
      payload: {
        message: message.trim(),
        type: activeTab,
        contactId,
        userId:user?._id,
        direction:"outbound",
        fromNumber:sim?.sim_number || sim?.sim_number,
        toNumber:contact?.phone,
        simId:sim?._id
      },
    });
    // });
    setMessage("");
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
 <div className="w-full bg-white dark:bg-gray-800 border-t dark:border-gray-700 rounded-t-xl">
  {/* Tabs Header */}
  <div className="flex items-center justify-between border-b dark:border-gray-700 px-3 py-1 flex-wrap gap-2">
    {/* Tabs */}
    <div
      ref={scrollRef}
      className="flex gap-4 overflow-x-auto whitespace-nowrap flex-1 scrollbar-hide"
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {tabs.map((tab, index) => (
        <button
          key={index}
          onClick={() => setActiveTab(tab?.name)}
          className={`pb-2 px-2 text-sm font-medium border-b-2 transition-all ${
            activeTab === tab?.name
              ? "border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400"
              : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
          }`}
        >
          {tab?.name}
        </button>
      ))}
    </div>

    {/* Scroll Arrows (only show when overflow) */}
    {showArrows && (
      <div className="flex items-center gap-1 ml-2 shrink-0">
        <button
          onClick={() => scrollTabs("left")}
          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={() => scrollTabs("right")}
          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    )}

    {/* Internal Comment */}
    <button className="text-gray-600 dark:text-gray-400 border dark:border-gray-600 rounded-lg px-3 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
      Internal Comment
    </button>
  </div>

  {/* From / To Line */}
  <div className="flex flex-wrap justify-between items-center px-5 py-2 border-b dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
    <div className="flex items-center space-x-2">
      <span className="font-medium">From:</span>
      <button className="text-gray-600 dark:text-gray-400 border dark:border-gray-600 rounded px-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700">
        {user?.connectedSim?.sim_number || subaccount?.defaultSim?.sim_number || " No Number Available"}
      </button>
    </div>
    <div className="flex items-center space-x-2 mt-2 sm:mt-0">
      <span className="font-medium">To:</span>
      <button className="text-gray-800 dark:text-gray-200 font-semibold border dark:border-gray-600 rounded px-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700">
        {contact?.phone || " No Number Available"}
      </button>
    </div>
  </div>

  {/* Message Input Field */}
  <div className="px-5 py-3 border-b dark:border-gray-700">
    <textarea
      placeholder={`Type a message (${activeTab})`}
      rows={3}
      className="w-full border dark:border-gray-600 rounded-lg px-4 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
      value={message} // Bind to message state
      onChange={(e) => setMessage(e.target.value)} // Update message state
      onKeyDown={handleKeyPress} // Handle Enter key
    ></textarea>
  </div>

  {/* Footer Actions */}
  <div className="flex items-center justify-between px-5 py-3 flex-wrap gap-3">
    {/* Left icons */}
    <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
      <Paperclip className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400" size={20} />
      <Smile className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400" size={20} />
      <DollarSign
        className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
        size={20}
      />
    </div>

    {/* Right actions */}
    <div className="flex items-center gap-3">
      <Plus
        className="cursor-pointer text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
        size={20}
      />
      <button
        onClick={handleSend} // Add onClick handler
        disabled={!message.trim() || !contact?.phone || (!user?.connectedSim?.sim_number && !subaccount?.defaultSim?.sim_number)} // Disable when empty
        className="flex items-center gap-1 bg-blue-500 dark:bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
      >
        <Send size={16} /> <span>Send</span>
      </button>
    </div>
  </div>
</div>
  );
};

export default ChatFooter;
